import { useState, useEffect, useRef, useCallback, useMemo } from "react";

import { FetchedTag } from "@/services/api/fetchers/tags";
import { FocusableTagCard } from "./TagCard";
import { PageDataPlaceholder } from "@/shared/components/ui/PageDataPlaceholder";
import { TagSimpleIcon } from "@phosphor-icons/react";
import usePrevious from "@/shared/hooks/usePrevious";
import { TagAction } from "../types";

interface TagsGridProps {
  actions: (tag: FetchedTag) => TagAction[];
  isOpen: boolean;
  tags: FetchedTag[];
  onTagPress: (tagId: number) => void;
  onOpenChange: (open: boolean) => void;
}

export function TagsGrid({ actions, tags, onTagPress }: TagsGridProps) {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevItemsLength = usePrevious(tags.length);
  const isNavigatingRef = useRef<boolean>(false);

  // Calculate total items for navigation
  const totalItems = tags.length;

  // Calculate grid dimensions
  const getGridDimensions = useCallback(() => {
    const cols = window.innerWidth >= 768 ? 4 : window.innerWidth >= 640 ? 3 : 2;
    return { cols };
  }, []);

  const [gridDimensions, setGridDimensions] = useState(getGridDimensions);

  // Create virtual rows for tags
  const tagRows = useMemo(() => {
    const { cols } = gridDimensions;
    const rows = [];
    for (let i = 0; i < tags.length; i += cols) {
      rows.push(tags.slice(i, i + cols));
    }
    return rows;
  }, [tags, gridDimensions]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!containerRef.current || totalItems === 0) return;

      // Focus first card if arrow key is pressed and no card is focused
      if (
        focusedIndex === -1 &&
        totalItems > 0 &&
        ["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", "h", "j", "k", "l"].includes(e.key)
      ) {
        setFocusedIndex(0);

        // Focus the first card using data attribute
        requestAnimationFrame(() => {
          const firstCard = containerRef.current?.querySelector(`[data-tag-index="0"]`) as HTMLElement;
          if (firstCard) {
            firstCard.focus();
          }
        });
        e.preventDefault();
        return;
      }

      const { cols } = gridDimensions;
      let newIndex = focusedIndex;

      switch (e.key) {
        case "ArrowRight":
        case "l":
          newIndex = Math.min(focusedIndex + 1, totalItems - 1);
          break;
        case "ArrowLeft":
        case "h":
          newIndex = Math.max(focusedIndex - 1, 0);
          break;
        case "ArrowDown":
        case "j":
          newIndex = Math.min(focusedIndex + cols, totalItems - 1);
          break;
        case "ArrowUp":
        case "k":
          newIndex = Math.max(focusedIndex - cols, 0);
          break;

        default:
          return;
      }

      if (newIndex !== focusedIndex) {
        // Prevent rapid-fire navigation
        if (isNavigatingRef.current) {
          return;
        }

        isNavigatingRef.current = true;

        setFocusedIndex(newIndex);

        // Focus the card using single requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          const targetCard = containerRef.current?.querySelector(`[data-tag-index="${newIndex}"]`) as HTMLElement;
          if (targetCard) {
            targetCard.focus();
            // Scroll into view if needed
            targetCard.scrollIntoView({ block: "nearest", behavior: "smooth" });
          }
          // Reset navigation flag after focus is complete
          isNavigatingRef.current = false;
        });

        e.preventDefault();
      }
    },
    [focusedIndex, totalItems, gridDimensions, setFocusedIndex]
  );

  // Update grid dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      setGridDimensions(getGridDimensions());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getGridDimensions]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Reset focused index when items are filtered
  useEffect(() => {
    if (prevItemsLength && prevItemsLength !== totalItems) {
      setFocusedIndex(-1);
    }
  }, [totalItems, prevItemsLength, setFocusedIndex]);

  // On mount, if focused index is different from -1, focus the card corresponding to the focused index
  useEffect(() => {
    if (focusedIndex !== -1 && containerRef.current) {
      // Delay focus to ensure components have rendered
      const attemptFocus = () => {
        const card = containerRef.current?.querySelector(`[data-tag-index="${focusedIndex}"]`) as HTMLElement;
        if (card) {
          card.focus();
          return true;
        }
        return false;
      };

      // Try immediate focus first
      if (!attemptFocus()) {
        // If immediate focus fails, wait for components to render
        const timeoutId = setTimeout(() => {
          attemptFocus();
        }, 100);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [focusedIndex]);

  if (tags.length === 0) {
    return (
      <PageDataPlaceholder icon={<TagSimpleIcon />} title="No Tags" description="Create a new tag to get started." />
    );
  }

  return (
    <>
      <div ref={containerRef} className="space-y-8" tabIndex={-1}>
        {tagRows.map((tagRow, rowIndex) => (
          <div key={`tag-row-${rowIndex}`} className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {tagRow.map((tag, colIndex) => {
              const itemIndex = rowIndex * gridDimensions.cols + colIndex;
              return (
                <FocusableTagCard
                  key={`tag-${tag.id}`}
                  actions={actions}
                  tag={tag}
                  index={itemIndex}
                  isFocused={itemIndex === focusedIndex}
                  onFocus={() => setFocusedIndex(itemIndex)}
                  onPress={() => onTagPress(tag.id)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}
